import { createSlice, createAsyncThunk, createSelector, PayloadAction } from '@reduxjs/toolkit';
import { Report, ReportQueryResult, SortConfig, FilterRule, extractVariables, PREDEFINED_VARIABLES } from '@bi-studio/shared/lib';
import { getReport, executeReport, getCompany } from '@bi-studio/shared/apis';
import { RootState } from '../index';

interface ReportState {
    report: Report | null;
    previewResult: ReportQueryResult | null;
    variableValues: Record<string, string>;
    requiredVariables: string[];
    sortConfigs: SortConfig[];
    columnFilters: Record<string, string | string[]>;
    advancedFilters: FilterRule[];
    page: number;
    pageSize: number;
    cachingPolicies: {
        enableCaching?: boolean;
        autoCaching?: boolean;
        webUsersCaching?: boolean;
    };
    isLoading: boolean;
    isExecuting: boolean;
    isExporting: boolean;
    error: string | null;
}

const initialState: ReportState = {
    report: null,
    previewResult: null,
    variableValues: {},
    requiredVariables: [],
    sortConfigs: [],
    columnFilters: {},
    advancedFilters: [],
    page: 1,
    pageSize: 50,
    cachingPolicies: {},
    isLoading: false,
    isExecuting: false,
    isExporting: false,
    error: null,
};

export const fetchReportDetails = createAsyncThunk(
    'report/fetchDetails',
    async ({ token, reportId, companyId }: { token: string; reportId: string; companyId?: string }, { rejectWithValue }) => {
        try {
            const report = await getReport(token, reportId);
            let cachingPolicies = {};
            if (companyId) {
                try {
                    const company = await getCompany(token, companyId);
                    cachingPolicies = company.cachingPolicies || {};
                } catch (e) {
                    console.error("Failed to load company policies", e);
                }
            }

            // Extract variables
            const allVars = extractVariables(report.query);
            const requiredVariables = allVars.filter(v => !PREDEFINED_VARIABLES.includes(v));

            return { report, cachingPolicies, requiredVariables };
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch report details');
        }
    }
);

export const executeReportDataThunk = createAsyncThunk(
    'report/executeData',
    async (
        { token, reportId, variables, cache, pagination }: {
            token: string;
            reportId: string;
            variables: Record<string, string>;
            cache: boolean;
            pagination: { page: number; pageSize: number };
        },
        { rejectWithValue }
    ) => {
        try {
            const result = await executeReport(token, reportId, {
                variables,
                cache: { enabled: cache },
                pagination
            });
            return { result, variables, pagination };
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to execute report');
        }
    }
);

export const reportSlice = createSlice({
    name: 'report',
    initialState,
    reducers: {
        setVariableValues: (state, action: PayloadAction<Record<string, string>>) => {
            state.variableValues = action.payload;
        },
        setSortConfigs: (state, action: PayloadAction<SortConfig[]>) => {
            state.sortConfigs = action.payload;
        },
        setColumnFilters: (state, action: PayloadAction<Record<string, string | string[]>>) => {
            state.columnFilters = action.payload;
        },
        setAdvancedFilters: (state, action: PayloadAction<FilterRule[]>) => {
            state.advancedFilters = action.payload;
        },
        setPage: (state, action: PayloadAction<number>) => {
            state.page = action.payload;
        },
        setIsExporting: (state, action: PayloadAction<boolean>) => {
            state.isExporting = action.payload;
        },
        resetReportState: (_state) => {
            return initialState;
        },
        setReportError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchReportDetails.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchReportDetails.fulfilled, (state, action) => {
                state.isLoading = false;
                state.report = action.payload.report;
                state.cachingPolicies = action.payload.cachingPolicies;
                state.requiredVariables = action.payload.requiredVariables;
            })
            .addCase(fetchReportDetails.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            .addCase(executeReportDataThunk.pending, (state) => {
                state.isExecuting = true;
                state.error = null;
            })
            .addCase(executeReportDataThunk.fulfilled, (state, action) => {
                state.isExecuting = false;
                state.previewResult = action.payload.result;
                state.variableValues = action.payload.variables;
                state.page = action.payload.pagination.page;
            })
            .addCase(executeReportDataThunk.rejected, (state, action) => {
                state.isExecuting = false;
                state.error = action.payload as string;
            });
    },
});

export const {
    setVariableValues,
    setSortConfigs,
    setColumnFilters,
    setAdvancedFilters,
    setPage,
    setIsExporting,
    resetReportState,
    setReportError
} = reportSlice.actions;

// Selectors
export const selectReportFull = (state: RootState) => state.report;
export const selectReportMetadata = (state: RootState) => state.report.report;
export const selectPreviewResult = (state: RootState) => state.report.previewResult;
export const selectReportLoading = (state: RootState) => state.report.isLoading;
export const selectReportExecuting = (state: RootState) => state.report.isExecuting;
export const selectReportError = (state: RootState) => state.report.error;
export const selectReportPagination = (state: RootState) => ({
    page: state.report.page,
    pageSize: state.report.pageSize,
});

export const selectFilteredRows = createSelector(
    [
        (state: RootState) => state.report.previewResult?.rows,
        (state: RootState) => state.report.columnFilters,
        (state: RootState) => state.report.advancedFilters
    ],
    (rows: any[] | undefined, columnFilters: Record<string, any>, advancedFilters: FilterRule[]) => {
        if (!rows) return [];

        let filteredRows = [...rows];

        // Apply column filters
        if (Object.keys(columnFilters).length > 0) {
            filteredRows = filteredRows.filter(row => {
                return Object.entries(columnFilters).every(([colName, filterValue]) => {
                    if (!filterValue || (Array.isArray(filterValue) && filterValue.length === 0)) return true;
                    const cellValue = row[colName];

                    if (Array.isArray(filterValue)) {
                        return filterValue.includes(String(cellValue));
                    }
                    const trimmedFilter = String(filterValue).trim();
                    if (!trimmedFilter) return true;

                    // Basic numeric/wildcard match
                    const operatorMatch = trimmedFilter.match(/^([><]=?|==|!=)?\s*(-?\d*\.?\d+)$/);
                    if (operatorMatch) {
                        const operator = operatorMatch[1] || "==";
                        const filterNum = parseFloat(operatorMatch[2]);
                        const cellNum = typeof cellValue === 'number' ? cellValue : parseFloat(String(cellValue));
                        if (!isNaN(cellNum)) {
                            switch (operator) {
                                case ">": return cellNum > filterNum;
                                case "<": return cellNum < filterNum;
                                case ">=": return cellNum >= filterNum;
                                case "<=": return cellNum <= filterNum;
                                case "==": return cellNum === filterNum;
                                case "!=": return cellNum !== filterNum;
                            }
                        }
                    }

                    const rangeMatch = trimmedFilter.match(/^(-?\d*\.?\d+)\.\.(-?\d*\.?\d+)$/);
                    if (rangeMatch) {
                        const min = parseFloat(rangeMatch[1]);
                        const max = parseFloat(rangeMatch[2]);
                        const cellNum = typeof cellValue === 'number' ? cellValue : parseFloat(String(cellValue));
                        if (!isNaN(cellNum)) return cellNum >= min && cellNum <= max;
                    }

                    if (trimmedFilter.includes("%")) {
                        const regexString = trimmedFilter.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/%/g, '.*');
                        return new RegExp(`^${regexString}$`, 'i').test(String(cellValue ?? ""));
                    }

                    return String(cellValue ?? "").toLowerCase().includes(trimmedFilter.toLowerCase());
                });
            });
        }

        // Apply advanced filters
        if (advancedFilters.length > 0) {
            filteredRows = filteredRows.filter(row => {
                return advancedFilters.every(rule => {
                    const cellValue = row[rule.columnName];
                    const op = rule.operator;
                    const val = rule.value;
                    if (op === 'is_null') return cellValue === null || cellValue === undefined || cellValue === '';
                    if (op === 'is_not_null') return cellValue !== null && cellValue !== undefined && cellValue !== '';
                    if (cellValue === null || cellValue === undefined) return false;

                    const stringCellValue = String(cellValue).toLowerCase();
                    const stringVal = String(val).toLowerCase();

                    switch (op) {
                        case 'equals': return String(cellValue) === String(val);
                        case 'not_equals': return String(cellValue) !== String(val);
                        case 'contains': return stringCellValue.includes(stringVal);
                        case 'not_contains': return !stringCellValue.includes(stringVal);
                        case 'starts_with': return stringCellValue.startsWith(stringVal);
                        case 'ends_with': return stringCellValue.endsWith(stringVal);
                        case 'greater_than': return Number(cellValue) > Number(val);
                        case 'less_than': return Number(cellValue) < Number(val);
                        case 'greater_than_or_equal': return Number(cellValue) >= Number(val);
                        case 'less_than_or_equal': return Number(cellValue) <= Number(val);
                        case 'between': {
                            const [min, max] = Array.isArray(val) ? val : [null, null];
                            const num = Number(cellValue);
                            if (min !== null && num < Number(min)) return false;
                            if (max !== null && num > Number(max)) return false;
                            return true;
                        }
                        case 'in': return (Array.isArray(val) ? val : []).includes(String(cellValue));
                        default: return true;
                    }
                });
            });
        }

        return filteredRows;
    }
);

export const selectSortedRows = createSelector(
    [
        selectFilteredRows,
        (state: RootState) => state.report.sortConfigs
    ],
    (filteredRows: any[], sortConfigs: SortConfig[]) => {
        if (sortConfigs.length === 0) return filteredRows;

        return [...filteredRows].sort((a, b) => {
            for (const config of sortConfigs) {
                const valA = a[config.columnName];
                const valB = b[config.columnName];
                if (valA === valB) continue;
                if (valA === null || valA === undefined) return 1;
                if (valB === null || valB === undefined) return -1;
                const comparison = valA < valB ? -1 : 1;
                return config.order === "asc" ? comparison : -comparison;
            }
            return 0;
        });
    }
);

export default reportSlice.reducer;
